import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import moment from 'moment';

import {
    SET_SESSION,
    ADD_SESSION_ID,
    RESET_SESSION_IDS,
} from '../../store/actions';
import SessionCard from '../../components/session/session-card';
import SessionRow from '../../components/session/session-row';
import GroupSessionCard from '../../components/group-session/group-session-card';
import GroupSessionRow from '../../components/group-session/group-session-row';
import {
    Box,
    Button,
    Container,
    Grid,
    Modal,
    Stack,
    Typography,
} from '@mui/material';
import { get } from '../../services/api.services';
import { ACCOUNT_TYPES, API } from '../../constants';
import {
    NoResults,
    PageTitle,
} from '../sessions/sessions.style';
import FolderCard, { FOLDER_VARIANTS } from '../../components/folder/folder-card';
import Invite from '../../components/folder/invite';
import { sessionIdsReadyToSchedule } from '../../utils/sessions';
import Module from './module';

function ExpertDashboard() {
    const dispatcher = useDispatch();
    const navigate = useNavigate();

    const user = useSelector((state) => state.account.user);
    const token = useSelector((state) => state.account.token);
    const sessionIds = useSelector((state) => state.sessions.sessionIds);
    const sessionsById = useSelector((state) => state.sessions.sessionsById);

    const [openInviteModal, setOpenInviteModal] = useState(false);
    const [products, setProducts] = useState([]);
    const [folders, setFolders] = useState([]);

    const fetchSessions = async () => {
        const result = await get(
            `${API.SESSION}`,
            token,
        );

        if (result.status === 403 || result.status === 401) {
            navigate('/login/experts');
        }
        else if (result.status === 200) {
            dispatcher({
                type: RESET_SESSION_IDS,
                payload: {},
            });

            result.data.forEach((session) => {
                dispatcher({
                    type: SET_SESSION,
                    payload: {
                        session,
                    },
                });
                dispatcher({
                    type: ADD_SESSION_ID,
                    payload: {
                        session,
                    },
                });
            });
        }
        else {
        }
    }

    const fetchProducts = async () => {
        const result = await get(
            `${API.PRODUCT}?seller_id=${user.profile.id}&product_type=group`,
            token,
        );
        
        if (result.status === 403 || result.status === 401) {
            navigate('/login/experts');
        }
        else if (result.status === 200) {
            setProducts(result.data.products);
        }
        else {
            // TODO: Handle errors
        }
    }

    const fetchFolders = async () => {
        const result = await get(
            API.FOLDER,
            token,
        );
        
        if (result.status === 403 || result.status === 401) {
            navigate('/login/experts');
        }
        else if (result.status === 200) {
            setFolders(result.data);
        }
        else {
            // TODO: Handle errors
        }
    }

    const closeInviteModal = () => {
        setOpenInviteModal(false);
        fetchFolders();
    }  

    const upcomingGroupSessions = products.filter((product) => moment(product.event_date) >= moment().subtract(product.duration || 30, 'minutes'));
    const finishedGroupSessions = products.filter((product) => moment(product.event_date) < moment().subtract(product.duration || 30, 'minutes'));

    useEffect(() => {
        fetchSessions();
        fetchProducts();
        fetchFolders();
    }, []);

    const filteredSessionIds = sessionIdsReadyToSchedule(sessionIds, sessionsById);    
    const sessionRequests = filteredSessionIds.filter((id) => !sessionsById[id].canceled && !sessionsById[id].session_date);
    const pendingSessions = sessionIds.filter((id) => !sessionsById[id].canceled && (moment(sessionsById[id].session_date) > moment().subtract(30, 'minutes')));
    const finishedSessions = sessionIds.filter((id) => sessionsById[id].canceled || (sessionsById[id].session_date && moment(sessionsById[id].session_date) < moment().subtract(30, 'minutes')));

    return (
        <Container maxWidth="md">
            <Box>
                <PageTitle>
                    For You on Nessle
                </PageTitle>
                <Box p={2} borderRadius={2} sx={{ backgroundColor: 'secondary.main' }}>
                    {user.profile.under_review && (
                        <>
                            <Box mb={2}>
                                <Typography variant="body1">
                                    <b>Start here:</b><br />
                                    Follow this guide to go from sign-up to client-ready in just 15 minutes. When you're done setting up your storefront, we'll activate your Nessle page and you can start welcoming customers.
                                </Typography>
                            </Box>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6} md={4}>
                                    <Module
                                        title="Build your Nessle storefront and listing page!"
                                        category="Get Up and Running"
                                        moduleKey="build"
                                    />
                                </Grid>
                            </Grid>
                        </>
                    )}
                    {!user.profile.under_review && (
                        <Grid container spacing={2}>
                            {/*<Grid item xs={12} sm={6} md={4}>
                                <Module
                                    title="Showcase testimonials from happy clients!"
                                    minutes={5}
                                    moduleKey="testimonials"
                                />
                            </Grid>*/}
                            <Grid item xs={12} sm={6} md={4}>
                                <Module
                                    title="Kickstart your client growth by telling the world you’re on Nessle!"
                                    category="Get More Clients"
                                    moduleKey="share"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <Module
                                    title="Prepare for your next session on the Nessle platform"
                                    category="Work with Clients"
                                    moduleKey="prepare"
                                />
                            </Grid>
                            {!user.profile.onboarding_checklist?.connect_bank && (
                                <Grid item xs={12} sm={6} md={4}>
                                    <Module
                                        title="Upload bank information when you’re ready to accept transactions"
                                        category="Get Up and Running"
                                        moduleKey="bank"
                                    />
                                </Grid>
                            )}
                            <Grid item xs={12} sm={6} md={4}>
                                <Module
                                    title="Get ideas about how to maximize your use of the Nessle platform!"
                                    category="Optimize"
                                    moduleKey="ideas"
                                />
                            </Grid>
                        </Grid>
                    )}
                </Box>
            </Box>
            <Box display="flex" alignItems="center">
                <PageTitle>
                    My Clients
                </PageTitle>
                {folders.length > 0 && (
                    <Box ml={1}>
                        <Button
                            color="primary"
                            variant="contained"
                            size="small"
                            onClick={() => setOpenInviteModal(true)}
                        >
                            + Add Client
                        </Button>
                        <Button
                            variant="text"
                            style={{ color: '#000000', textTransform: 'none', textDecoration: 'underline', marginLeft: 5 }}
                            onClick={() => {
                                navigate('/clients')
                            }}
                        >
                            View All
                        </Button>
                    </Box>
                )}
            </Box>
            <Grid container spacing={2}>
                {folders.length === 0 && (
                    <Grid item xs={12}>
                        <NoResults>
                            Start managing your clients on Nessle
                            <Box mt={2}>
                                <Button variant="contained" color="primary" onClick={() => setOpenInviteModal(true)}>
                                    Invite a client
                                </Button>
                            </Box>
                        </NoResults>
                    </Grid>
                )}
                {folders.filter((folder) => folder.folder_type === 'CLIENT').slice(0, 4).map((folder) => (
                    <Grid key={folder.id} item xs={12} md={6}>
                        <FolderCard
                            folder={folder}
                            variant={folder.owner.id === user.profile.id ? FOLDER_VARIANTS.OWNER : FOLDER_VARIANTS.MEMBER}
                        />
                    </Grid>
                ))}
            </Grid>
            {sessionRequests.length > 0 && (
                <>
                    <PageTitle>
                        Session Requests
                    </PageTitle>
                    <Grid container spacing={2}>
                        {sessionRequests.map((id) => (
                            <Grid key={id} item xs={12} sm={6}>
                                <SessionCard
                                    session={sessionsById[id]}
                                    variant={sessionsById[id].parent_profile.id === user.profile.id ? ACCOUNT_TYPES.PARENT : ACCOUNT_TYPES.EXPERT}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </>
            )}
            <PageTitle>
                Upcoming Sessions
            </PageTitle>
            <Grid container spacing={2}>
                {pendingSessions.length === 0 && upcomingGroupSessions.length === 0 && (
                    <Grid item xs={12}>
                        <NoResults>You don't have any pending sessions yet</NoResults>
                    </Grid>
                )}
                {pendingSessions.map((id) => (
                    <Grid key={id} item xs={12} sm={6}>
                        <SessionCard
                            session={sessionsById[id]}
                            variant={sessionsById[id].parent_profile.id === user.profile.id ? ACCOUNT_TYPES.PARENT : ACCOUNT_TYPES.EXPERT}
                        />
                    </Grid>
                ))}
                {upcomingGroupSessions.map((product) => (
                    <Grid key={product.id} item xs={12} sm={6}>
                        <GroupSessionCard
                            product={product}
                            variant={user.profile.profile_type}
                        />
                    </Grid>
                ))}
            </Grid>
            <Box sx={{ mb: 2 }}>
                <PageTitle>
                    Recent Sessions
                </PageTitle>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Stack spacing={1}>
                            {finishedSessions.length === 0 && finishedGroupSessions.length === 0 && (
                                <Grid item xs={12}>
                                    <NoResults>You don't have completed sessions yet</NoResults>
                                </Grid>
                            )}
                            {finishedSessions.map((id) => (
                                <SessionRow
                                    key={id}
                                    session={sessionsById[id]}
                                    variant={sessionsById[id].parent_profile.id === user.profile.id ? ACCOUNT_TYPES.PARENT : ACCOUNT_TYPES.EXPERT}
                                />
                            ))}
                            {finishedGroupSessions.map((product) => (
                                <Grid key={product.id} item xs={12}>
                                    <GroupSessionRow
                                        product={product}
                                        variant={user.profile_type}
                                    />
                                </Grid>
                            ))}
                        </Stack>
                    </Grid>
                </Grid>
            </Box>
            <Modal
                open={openInviteModal}
                onClose={closeInviteModal}
            >   
                <Invite onClose={closeInviteModal} />
            </Modal>
        </Container>
    );
}

export default ExpertDashboard;